import React, { useState } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../../ui/Dialog';
import { Button } from '../../ui/Button';
import SelectMenu from '../../ui/SelectMenu';
import { Select } from '../../ui/Input';
import { ArrowRightIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const BIDIT_FIELDS = [
  { value: '', label: '— Skip this column —' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'companyName', label: 'Company Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'street', label: 'Street Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'ZIP Code' },
  { value: 'type', label: 'Customer Type' },
];

const lc = (v) => String(v || '').trim().toLowerCase();

// IDI-667 D1: each file's headers map themselves ("type" too); the step
// remounts the dialog per file (key), so each file gets its own mapping.
function autoMap(headers) {
  const autoMap = {};
  headers.forEach((header, idx) => {
    const normalized = header.toLowerCase().replace(/[^a-z]/g, '');
    const match = BIDIT_FIELDS.find(f => {
      const fieldNorm = f.value.toLowerCase();
      return fieldNorm === normalized ||
        (normalized.includes('first') && f.value === 'firstName') ||
        (normalized.includes('last') && f.value === 'lastName') ||
        (normalized.includes('company') && f.value === 'companyName') ||
        (normalized.includes('email') && f.value === 'email') ||
        (normalized.includes('phone') && f.value === 'phone') ||
        (normalized.includes('street') && f.value === 'street') ||
        (normalized.includes('address') && !normalized.includes('city') && !normalized.includes('state') && !normalized.includes('zip') && f.value === 'street') ||
        (normalized.includes('city') && f.value === 'city') ||
        (normalized.includes('state') && f.value === 'state') ||
        (normalized.includes('zip') && f.value === 'zip') ||
        (normalized.includes('type') && f.value === 'type');
    });
    autoMap[idx] = match ? match.value : '';
  });
  return autoMap;
}

export function CSVMappingModal({
  open, onClose, onConfirm, csvHeaders = [], rows = [], typeOptions = [],
  importing = false, progress = { n: 0, N: 0 }, fileName = '',
}) {
  const [mapping, setMapping] = useState(() => autoMap(csvHeaders));
  const [pick, setPick] = useState('');
  const [pickError, setPickError] = useState('');

  // IDI-667 D4: type cells matching no Settings type (any case) are added;
  // rows without one take the pick below.
  const typeCol = Object.keys(mapping).find(k => mapping[k] === 'type');
  const cellType = (row) => (typeCol === undefined ? '' : String(row[typeCol] || '').trim());
  const known = new Set(typeOptions.map(o => lc(o.label)));
  const newTypes = [];
  rows.forEach(row => {
    const t = cellType(row);
    if (t && !known.has(lc(t)) && !newTypes.some(n => lc(n) === lc(t))) newTypes.push(t);
  });
  const hasUntyped = rows.some(row => !cellType(row));

  const [errors, setErrors] = useState([]);

  const handleMappingChange = (colIdx, value) => {
    setMapping(prev => ({ ...prev, [colIdx]: value }));
  };

  const getMappedCount = () => {
    return Object.values(mapping).filter(v => v !== '').length;
  };

  const hasDuplicates = () => {
    const mapped = Object.values(mapping).filter(v => v !== '');
    return mapped.length !== new Set(mapped).size;
  };

  const handleConfirm = () => {
    const newErrors = [];
    // Check for required fields
    const mappedValues = Object.values(mapping);
    if (!mappedValues.includes('firstName') && !mappedValues.includes('companyName')) {
      newErrors.push('Either "First Name" or "Company Name" must be mapped');
    }
    if (hasDuplicates()) {
      newErrors.push('Each BIDIT field can only be mapped once');
    }
    setErrors(newErrors);
    const missingPick = hasUntyped && !pick;
    setPickError(missingPick ? 'Customer type is required' : '');
    if (newErrors.length > 0 || missingPick) return;
    onConfirm(mapping, pick);
  };

  return (
    <Dialog open={open} onClose={importing ? () => {} : onClose} size="xl">
      <DialogHeader onClose={importing ? null : onClose}>
        Map CSV Columns — {fileName}
      </DialogHeader>
      <DialogBody className="max-h-[70vh] overflow-hidden flex flex-col">
        {/* Status bar */}
        <div className="flex items-center justify-between mb-4 px-1">
          <p className="text-sm text-gray-500">
            {getMappedCount()} of {csvHeaders.length} columns mapped
          </p>
          {hasDuplicates() && (
            <p className="text-sm text-amber-600 flex items-center gap-1">
              <ExclamationTriangleIcon className="h-4 w-4" />
              Duplicate mappings detected
            </p>
          )}
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="mb-4 bg-red-50 p-3" style={{ borderRadius: 'var(--radius-md)' }}>
            {errors.map((err, i) => (
              <p key={i} className="text-sm text-red-700">{err}</p>
            ))}
          </div>
        )}

        {/* Mapping Table */}
        <div className="overflow-y-auto flex-1 border" style={{ borderRadius: 'var(--radius-md)' }}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-700 w-1/3">CSV Column</th>
                <th className="px-4 py-2 w-8"></th>
                <th className="text-left px-4 py-2 font-medium text-gray-700 w-1/3">BIDIT Field</th>
                <th className="text-left px-4 py-2 font-medium text-gray-700">Preview</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {csvHeaders.map((header, idx) => (
                <tr key={idx} className={mapping[idx] ? 'bg-green-50/50' : ''}>
                  <td className="px-4 py-2">
                    <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                      {header}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                  </td>
                  <td className="px-4 py-2">
                    <SelectMenu
                      value={mapping[idx] || ''}
                      onChange={(val) => handleMappingChange(idx, val)}
                      options={BIDIT_FIELDS.map(f => ({ value: f.value, label: f.label }))}
                      className="w-full"
                    />
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs truncate max-w-[200px]">
                    {rows[0]?.[idx] || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Preview rows */}
        {(newTypes.length > 0 || hasUntyped) && (
          <div className="mt-4">
            {newTypes.length > 0 && (
              <p className="text-sm text-gray-700 mb-2">New customer types: {newTypes.join(', ')}</p>
            )}
            {hasUntyped && (
              <div className="max-w-xs">
                <p className="text-sm font-medium text-gray-700 mb-1">Customer type for rows without one</p>
                <Select aria-label="Customer type for rows without one" value={pick} error={pickError}
                  onChange={(e) => setPick(e.target.value)} disabled={importing}>
                  <option value="">Select type...</option>
                  {typeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </Select>
              </div>
            )}
          </div>
        )}

        {rows.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-500 mb-2">
              Preview: First {Math.min(rows.length, 3)} rows
            </p>
            <div className="overflow-x-auto border" style={{ borderRadius: 'var(--radius-md)' }}>
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    {csvHeaders.map((h, i) => (
                      <th key={i} className="px-3 py-1.5 text-left font-medium text-gray-600 whitespace-nowrap">
                        {mapping[i] ? (
                          <span className="text-green-700 flex items-center gap-1">
                            <CheckCircleIcon className="h-3 w-3" />
                            {BIDIT_FIELDS.find(f => f.value === mapping[i])?.label}
                          </span>
                        ) : (
                          <span className="text-gray-400">Skipped</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.slice(0, 3).map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} className={`px-3 py-1.5 whitespace-nowrap ${mapping[cellIdx] ? 'text-gray-900' : 'text-gray-300'}`}>
                          {cell || '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        {importing && (
          <p className="mr-auto text-sm text-gray-600">{`Importing ${progress.n} of ${progress.N}…`}</p>
        )}
        <Button variant="secondary" onClick={onClose} disabled={importing}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={importing} loading={importing}>
          Import {rows.length} Customer{rows.length !== 1 ? 's' : ''}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
